// Relances sur devis — la liste des devis envoyés et de leur suite.
//
// Vit dans la MESSAGERIE, et non dans le Pilotage : relancer un client est un
// acte de communication, pas de trésorerie. Les réglages du moteur de relances
// sont d'ailleurs déjà ici.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, Send, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { sendSms } from '@/modules/messagerie/api';
import {
  type DevisExterne,
  importerRelancesDevis,
  listDevisExternes,
  messageRelanceDevis,
  segmentsSms,
  type StatutDevis,
  STATUTS_DEVIS,
  updateDevisExterne,
} from '@/modules/messagerie/relances-api';

const EUR = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const FILTRES = [
  ['sans_reponse', 'Sans réponse'],
  ['en_attente', 'En attente'],
  ['accepte', 'Acceptés'],
  ['refuse', 'Refusés'],
  ['tous', 'Tous'],
] as const;

export function RelancesDevis() {
  const queryClient = useQueryClient();
  const [statut, setStatut] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<StatutDevis | 'tous'>('sans_reponse');
  const fichierRef = useRef<HTMLInputElement>(null);

  const devisQ = useQuery({ queryKey: ['devis-externes'], queryFn: listDevisExternes });
  const tout = useMemo(() => devisQ.data ?? [], [devisQ.data]);
  const liste = useMemo(
    () => (filtre === 'tous' ? tout : tout.filter((d) => d.statut === filtre)),
    [tout, filtre],
  );

  const majMut = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { statut?: StatutDevis; commentaire?: string };
    }) => updateDevisExterne(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis-externes'] });
    },
  });

  const importer = async (file: File) => {
    setStatut('Import du classeur en cours…');
    try {
      const r = await importerRelancesDevis(file);
      const details = [
        r.doublons > 0 ? `${String(r.doublons)} devis en double, comptés une fois` : null,
        r.datesIgnorees > 0
          ? `${String(r.datesIgnorees)} date(s) hors de l'année de leur feuille, écartée(s)`
          : null,
      ].filter(Boolean);
      setStatut(
        `✅ ${String(r.enregistres)} devis repris${details.length > 0 ? ` — ${details.join(', ')}` : ''}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['devis-externes'] });
    } catch (e) {
      setStatut(`Erreur d'import : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /**
   * Relance par SMS, une à la fois et sur décision : beaucoup de ces devis
   * datent de plusieurs années, et un envoi en masse serait mal reçu.
   */
  const relancerParSms = async (d: DevisExterne) => {
    if (!d.telephone) {
      setStatut('Ce devis n’a pas de numéro de téléphone dans le classeur.');
      return;
    }
    const corps = messageRelanceDevis(d);
    // Le coût est annoncé AVANT l'envoi : un SMS est facturé au segment, et
    // rien à l'écran ne laisserait deviner qu'un message en vaut deux.
    const n = segmentsSms(corps);
    const cout = `${String(corps.length)} caractères — ${String(n)} SMS facturé${n > 1 ? 's' : ''}`;
    if (!window.confirm(`Envoyer ce SMS au ${d.telephone} ?\n(${cout})\n\n${corps}`)) return;
    setStatut('Envoi du SMS…');
    const r = await sendSms({ telephone: d.telephone, corps });
    if (r.ok) {
      setStatut(`✅ SMS envoyé à ${d.client ?? d.telephone}.`);
      majMut.mutate({ id: d.id, patch: { statut: 'en_attente' } });
    } else if (r.skipped === 'opt_out') {
      setStatut(`⛔ ${d.client ?? d.telephone} s’est désinscrit (STOP) : aucun SMS envoyé.`);
    } else {
      setStatut(`Échec de l’envoi : ${r.error ?? 'raison inconnue'}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Relances devis</CardTitle>
          <div className="flex flex-wrap items-center gap-1">
            {FILTRES.map(([id, label]) => {
              const n = id === 'tous' ? tout.length : tout.filter((d) => d.statut === id).length;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setFiltre(id);
                  }}
                  className={`rounded-md px-2 py-1 text-xs font-medium ${
                    filtre === id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label} ({n})
                </button>
              );
            })}
            <Button variant="outline" size="sm" asChild>
              {/* Le même travail, en déplacement : une fiche à la fois et un
                  bouton qui compose le numéro. */}
              <Link to="/relances" title="Ouvrir l'écran d'appel, pensé pour le téléphone">
                <Phone className="h-4 w-4" /> Écran d&apos;appel
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              title="Reprendre le classeur RELANCES CLIENTS (.xlsx) — met à jour les devis déjà repris"
              onClick={() => fichierRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> Importer
            </Button>
            <input
              ref={fichierRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importer(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>
        <p className="text-muted-foreground text-xs">
          Devis du classeur de suivi, du plus ancien au plus récent. Le statut et le commentaire se
          modifient ici ; un réimport du classeur les écrase, celui-ci restant la source.
        </p>
        {statut ? <p className="text-muted-foreground text-sm">{statut}</p> : null}
      </CardHeader>
      <CardContent>
        {devisQ.isPending ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : liste.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Aucun devis dans cette vue. Utilisez « Importer » pour reprendre le classeur de suivi.
          </p>
        ) : (
          <>
            <div className="text-muted-foreground mb-2 text-sm">
              {liste.length} devis · {EUR.format(liste.reduce((t, d) => t + d.montant, 0))} €
            </div>
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full min-w-[900px] border-collapse text-[12.5px]">
                <thead className="bg-card sticky top-0">
                  <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
                    <th className="py-1.5 pr-2 font-medium">Envoyé le</th>
                    <th className="px-2 py-1.5 font-medium">N°</th>
                    <th className="px-2 py-1.5 font-medium">Client</th>
                    <th className="px-2 py-1.5 font-medium">Ville</th>
                    <th className="px-2 py-1.5 font-medium">Objet</th>
                    <th className="px-2 py-1.5 text-right font-medium">Montant</th>
                    <th className="px-2 py-1.5 font-medium">Suite</th>
                    <th className="px-2 py-1.5 font-medium">Commentaire</th>
                    <th className="px-2 py-1.5 font-medium">Relancer</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.map((d) => (
                    <tr key={d.id} className="border-muted/60 hover:bg-muted/30 border-b">
                      <td className="py-0.5 pr-2 tabular-nums">
                        {d.date_envoi ? (
                          new Date(`${d.date_envoi}T12:00:00`).toLocaleDateString('fr-FR')
                        ) : (
                          <span className="text-amber-700" title="Date absente du classeur">
                            —
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2">
                        {d.numero}
                        {d.dansLeCrm ? (
                          <span
                            className="ml-1 text-emerald-700"
                            title="Ce devis existe aussi dans le module Devis du CRM"
                          >
                            ✓
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2">
                        {d.client ?? <span className="text-amber-700">—</span>}
                      </td>
                      <td className="text-muted-foreground px-2">{d.ville ?? ''}</td>
                      <td className="max-w-[260px] truncate px-2" title={d.objet ?? ''}>
                        {d.objet ?? ''}
                      </td>
                      <td className="px-2 text-right tabular-nums">{EUR.format(d.montant)}</td>
                      <td className="px-2">
                        <select
                          value={d.statut}
                          className={`rounded px-1 py-0.5 text-[11px] font-medium ${
                            STATUTS_DEVIS.find((x) => x.id === d.statut)?.teinte ?? ''
                          }`}
                          onChange={(e) => {
                            majMut.mutate({
                              id: d.id,
                              patch: { statut: e.target.value as StatutDevis },
                            });
                          }}
                        >
                          {STATUTS_DEVIS.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2">
                        <input
                          key={`${d.id}-${d.commentaire ?? ''}`}
                          defaultValue={d.commentaire ?? ''}
                          placeholder="…"
                          className="hover:border-input focus:border-ring w-full min-w-[140px] rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (d.commentaire ?? '')) {
                              majMut.mutate({ id: d.id, patch: { commentaire: v } });
                            }
                          }}
                        />
                      </td>
                      <td className="px-2">
                        {d.telephone && d.statut !== 'accepte' && d.statut !== 'refuse' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title={`Relancer par SMS au ${d.telephone}`}
                            onClick={() => {
                              void relancerParSms(d);
                            }}
                          >
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <span
                            className="text-muted-foreground text-[11px]"
                            title={d.telephone ? 'Devis clos' : 'Aucun numéro dans le classeur'}
                          >
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
